import React, { useState, useEffect } from 'react';
import { Layers, Clock, Heart, Building2, Globe, Database, Cloud, Lock, Mail, ExternalLink, Sparkles, Sun, Moon } from 'lucide-react';

const TOOLS = [
  {
    name: 'DSR MATRIX Nexus',
    tagline: 'WORKFORCE INTELLIGENCE & CAPACITY PLANNING',
    description: 'Real-time allocation heatmaps, bench tracking, project financials, demand forecasting, and custom report builder — all in one intelligent platform.',
    icon: Layers,
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1, #a855f7)',
    features: ['Allocation Grid', 'Workforce', 'Projects', 'Pipeline', 'Approvals', 'Report Builder', 'Financials'],
    keywords: ['Plan', 'Allocate', 'Optimize'],
    url: 'https://dsrmatrix-nexus.web.app',
  },
  {
    name: 'DSR MATRIX ERP',
    tagline: 'COMPLETE MANUFACTURING ERP PLATFORM',
    description: 'End-to-end operations visibility: HR, Finance, Supply Chain, Production, Quality, Sales, and procurement in one cohesive experience.',
    icon: Building2,
    color: '#2563eb',
    gradient: 'linear-gradient(135deg, #2563eb, #3b82f6)',
    features: ['Dashboard', 'HR & Payroll', 'Finance', 'Inventory', 'Quality', 'Sales'],
    keywords: ['Manage', 'Produce', 'Deliver'],
    url: 'https://dsrmatrix-erp.web.app',
  },
  {
    name: 'DSR MATRIX CareForce',
    tagline: 'HEALTHCARE WORKFORCE PLATFORM',
    description: 'Shift scheduling, swap management, call-off cascades, payroll, credentialing, budget tracking, and compliance — purpose-built for healthcare facilities.',
    icon: Heart,
    color: '#00ddb3',
    gradient: 'linear-gradient(135deg, #00ddb3, #0088cc)',
    features: ['Scheduling', 'Staff Directory', 'Call-Off Cascade', 'Payroll', 'Credentials', 'Compliance', 'Reports'],
    keywords: ['Schedule', 'Staff', 'Optimize'],
    url: 'https://dsrmatrix-careforce.web.app',
  },
  {
    name: 'DSR MATRIX Sync',
    tagline: 'TIME & ATTENDANCE MANAGEMENT',
    description: 'Frictionless timesheets, real-time attendance tracking, team presence monitoring, leave management, and automated approvals — easy enough for anyone.',
    icon: Clock,
    color: '#14b8a6',
    gradient: 'linear-gradient(135deg, #14b8a6, #22d3ee)',
    features: ['Timesheets', 'Attendance', 'Team Presence', 'Approvals', 'Leave Mgmt', 'Reports'],
    keywords: ['Track', 'Sync', 'Report'],
    url: 'https://dsrmatrix-sync.web.app',
  },
];

const WL_FEATURES = [
  { icon: Globe, label: 'Your Brand & Domain', desc: 'Custom logo, colors, hosted on your domain' },
  { icon: Database, label: 'Dedicated Database', desc: 'Isolated, encrypted data — 100% yours' },
  { icon: Cloud, label: 'Cloud Hosted', desc: 'Deployed on secure cloud infrastructure' },
  { icon: Lock, label: 'Enterprise Security', desc: 'SOC2-ready, encrypted, role-based access' },
];

function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('dsrmatrix_theme') || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('dsrmatrix_theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  return (
    <div className="marketplace-container">
      {/* Theme Toggle */}
      <button className="theme-toggle" onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* Hero */}
      <div className="hero">
        <div className="hero-logo">DM</div>
        <h1>DSR MATRIX</h1>
        <div className="subtitle">Software Tools Hub — Marketplace</div>
        <p className="description">
          Explore our enterprise software tools. Each tool works independently with full demo data — try them live, no signup required.
        </p>
      </div>

      {/* Tools Grid */}
      <div className="tools-grid">
        {TOOLS.map((tool) => {
          const Icon = tool.icon;
          return (
            <a
              key={tool.name}
              href={tool.url}
              target="_blank"
              rel="noopener noreferrer"
              className="tool-card"
              style={{ borderTop: `3px solid ${tool.color}` }}
            >
              <div className="tool-icon" style={{ background: `${tool.color}15`, color: tool.color }}>
                <Icon size={22} />
              </div>
              <h3 style={{ color: tool.color }}>{tool.name}</h3>
              <div className="tool-tagline">{tool.tagline}</div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {tool.keywords.map((kw, i) => (
                  <span key={i} style={{ fontSize: 11, fontWeight: 600, color: tool.color }}>
                    {kw}{i < tool.keywords.length - 1 ? ' ·' : ''}
                  </span>
                ))}
              </div>
              <p className="tool-desc">{tool.description}</p>
              <div className="tool-features">
                {tool.features.map((f, i) => (
                  <span key={i} className="tool-feature" style={{ background: `${tool.color}12`, color: tool.color }}>
                    {f}
                  </span>
                ))}
              </div>
              <span className="tool-link" style={{ color: tool.color }}>
                Launch {tool.name.replace('DSR MATRIX ', '')} <ExternalLink size={13} />
              </span>
            </a>
          );
        })}
      </div>

      {/* White-Label Section */}
      <div className="whitelabel-section">
        <div className="whitelabel-card">
          <div className="whitelabel-badge">
            <Sparkles size={12} /> White-Label Available
          </div>
          <h3>Want to use these tools for your organization?</h3>
          <p>
            DSR MATRIX builds customizable enterprise software. Every tool on this marketplace can be white-labeled with your company branding and deployed on your own domain.
          </p>
          <div className="wl-features">
            {WL_FEATURES.map((f, i) => {
              const FIcon = f.icon;
              return (
                <div key={i} className="wl-feature">
                  <div className="wl-feature-icon">
                    <FIcon size={14} style={{ color: '#a78bfa' }} />
                  </div>
                  <div>
                    <h4>{f.label}</h4>
                    <p>{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <a href="mailto:support@dsrmatrix.com" className="contact-btn">
            <Mail size={16} /> Contact DSR MATRIX →
          </a>
          <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center', gap: 20, flexWrap: 'wrap' }}>
            <a href="mailto:support@dsrmatrix.com" style={{ fontSize: 11, color: 'var(--text-muted)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Mail size={10} /> support@dsrmatrix.com
            </a>
            <a href="https://dsrmatrix.com" target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--text-muted)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
              <ExternalLink size={10} /> dsrmatrix.com
            </a>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="about-section">
        <div className="about-card">
          <h3>About DSR MATRIX</h3>
          <p>
            We are a software company that builds enterprise-grade tools for workforce management, healthcare scheduling, manufacturing operations, and more. Our tools are designed to be white-labeled and customized for your organization — your brand, your domain, your data. We handle the technology so you can focus on your business.
          </p>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            {['Workforce Intelligence', 'Time & Attendance', 'Healthcare Scheduling', 'Manufacturing ERP', 'Custom Software'].map((s, i) => (
              <span key={i} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, background: 'var(--accent-glow)', color: 'var(--accent-light)', fontWeight: 500 }}>
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="marketplace-footer">
        <div className="footer-brand">
          <div style={{ marginBottom: 6 }}>Powered by <strong>DSR MATRIX</strong></div>
          <div>
            <a href="mailto:support@dsrmatrix.com">support@dsrmatrix.com</a>
            {' · '}
            <a href="https://dsrmatrix.com" target="_blank" rel="noreferrer">dsrmatrix.com</a>
          </div>
          <div style={{ marginTop: 8, fontSize: 10, opacity: 0.5 }}>
            © {new Date().getFullYear()} DSR MATRIX. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
